import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";

import PasswordInput from "../PasswordInput";

const SUPER_ADMIN_SLUG = "super-admin";

const ChangeUserRoleModal = ({
  show,
  onHide,
  currentUser,
  assignableRoles,
  rolesKnown = true,
  viewerCanAssignSuperAdmin,
  onSubmit,
  serverFieldErrors,
  onClearServerFieldError,
}) => {
  const currentRoleId = currentUser?.roleData?.id ?? currentUser?.role_id ?? "";
  const currentRoleName = currentUser?.roleData?.name ?? "their current role";

  const eligibleRoles = useMemo(() => {
    const roles = assignableRoles ?? [];
    if (viewerCanAssignSuperAdmin) return roles;
    return roles.filter((role) => role.slug !== SUPER_ADMIN_SLUG);
  }, [assignableRoles, viewerCanAssignSuperAdmin]);

  const hasRoles = eligibleRoles.length > 0;
  const missingRoles = rolesKnown && !hasRoles;

  const [roleId, setRoleId] = useState("");
  const [reason, setReason] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [roleError, setRoleError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setRoleId("");
      setReason("");
      setCurrentPassword("");
      setRoleError(null);
      setPasswordError(null);
    }
  }, [show, currentUser]);

  const roleIdServerError = serverFieldErrors?.role_id ?? null;
  const reasonServerError = serverFieldErrors?.reason ?? null;
  const currentPasswordServerError = serverFieldErrors?.current_password ?? null;

  const displayRoleError = roleError || roleIdServerError;
  const displayPasswordError = passwordError || currentPasswordServerError;

  const noOpSelected = roleId !== "" && roleId === currentRoleId;

  const clearRoleErrors = () => {
    setRoleError(null);
    if (roleIdServerError && onClearServerFieldError) onClearServerFieldError("role_id");
  };

  const clearPasswordErrors = () => {
    setPasswordError(null);
    if (currentPasswordServerError && onClearServerFieldError) onClearServerFieldError("current_password");
  };

  const clearReasonServerError = () => {
    if (reasonServerError && onClearServerFieldError) onClearServerFieldError("reason");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (missingRoles || !hasRoles) return;

    if (!roleId) {
      setRoleError("Please select a role.");
      return;
    }
    if (noOpSelected) {
      setRoleError("Select a different role. The user already has this role.");
      return;
    }
    if (!currentPassword.trim()) {
      setPasswordError("Please enter your current password to confirm this change.");
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ roleId, reason: reason.trim() || null, currentPassword });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change role: {currentUser?.full_name}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <p className="mb-3">
            Reassigns {currentUser?.first_name ?? "this user"} from {currentRoleName} to a new role.
            Any special permissions granted directly to them are cleared, and every active session
            they have is signed out.
          </p>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="change-user-role-select">New role</Form.Label>
            <Form.Select
              id="change-user-role-select"
              value={roleId}
              onChange={(event) => {
                setRoleId(event.target.value);
                clearRoleErrors();
              }}
              isInvalid={!!displayRoleError}
              disabled={submitting || missingRoles}
            >
              <option value="">Select a role</option>
              {eligibleRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{displayRoleError}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="change-user-role-reason">Reason (optional)</Form.Label>
            <Form.Control
              id="change-user-role-reason"
              as="textarea"
              rows={3}
              maxLength={500}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                clearReasonServerError();
              }}
              isInvalid={!!reasonServerError}
              disabled={submitting}
              placeholder="If provided, this appears in the notification email and the audit log."
            />
            <Form.Control.Feedback type="invalid">{reasonServerError}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="change-user-role-current-password">Current password</Form.Label>
            <PasswordInput
              id="change-user-role-current-password"
              className={displayPasswordError ? "form-control is-invalid" : "form-control"}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => {
                setCurrentPassword(event.target.value);
                clearPasswordErrors();
              }}
              disabled={submitting}
            />
            {displayPasswordError && (
              <div className="invalid-feedback d-block">{displayPasswordError}</div>
            )}
            <Form.Text className="text-muted">
              Confirm this change with your own current password.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || missingRoles}
            title={missingRoles ? "No roles are available to assign." : undefined}
          >
            {submitting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Update
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ChangeUserRoleModal;
